import { AfterViewInit, Component, ElementRef, InputSignal, NgModule, NgZone, ViewChild, WritableSignal, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AddressService, NavegoAddressRequest } from '../../address.service';
import { Loader } from '@googlemaps/js-api-loader';
import { DeliveryAddress } from '../../../authentication/signup/signup.types';
import { GoogleMapsUtils } from '../../common/utils';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from '../../../../environments/environment';
import { SignalsStoreService } from '../../signals-store.service';
import { LocalStorageService } from '../../local-storage.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { DataLayerService } from '../../data-layer/data-layer.service';
import { CheckAddressModalData } from '../modal-content';

@Component({
  selector: 'app-check-address',
  imports: [MatProgressBarModule, FormsModule],
  providers: [{
    provide: Loader,
    useValue: new Loader({
      apiKey: environment.apis.google.places,
      libraries: ['places'],
      region: 'US',
    })
  }],
  templateUrl: './check-address.component.html',
  styleUrl: './check-address.component.scss'
})
export class CheckAddressComponent implements AfterViewInit {

  @ViewChild('autocompleteInput') autocompleteInput!: ElementRef<HTMLInputElement>;
  localStorageService = inject(LocalStorageService)

  private router = inject(Router);
  private activeModal = inject(NgbActiveModal)
  private addressService = inject(AddressService);
  private zone = inject(NgZone);
  private loader = inject(Loader);
  private signalsStoreService = inject(SignalsStoreService);
  #dataLayerService = inject(DataLayerService);

  isGoogleAddressFilled = signal(false);
  isLoadingContent: any = computed(() => this.addressService.isLoading());
  hasGoogleAddress = computed(() => !!this.signalsStoreService.googleAddress());
  hasDeliveryAddressCoverage: any = signal(false);

  modalContentData: InputSignal<CheckAddressModalData | null> = input<CheckAddressModalData | null>(null)

  queryString = ''

  email = '';
  emailValidation: WritableSignal<{ valid: boolean, error: string }> = signal({ valid: true, error: '' });

  ngAfterViewInit(): void {
    const locationText: string | null =
      this.signalsStoreService.anonymousUserAddress()?.street ?? '';
    if (locationText) this.queryString = locationText;

    const savedEmail: string | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PRELOADED_EMAIL);
    if (savedEmail) {
      this.email = savedEmail;
      this.validateEmail(savedEmail);
    }

    GoogleMapsUtils.initializeAutocomplete(this.loader, this.autocompleteInput.nativeElement, this.onPlaceSelected.bind(this));
    GoogleMapsUtils.loadAutoCompleteIfAny(
      this.loader,
      this.onPlaceSelected.bind(this),
      this.localStorageService
    );
  }

  loginClickHandler() {
    this.activeModal.close();
    this.router.navigate(['/login']);
  }

  private onPlaceSelected(place: google.maps.places.PlaceResult) {
    this.zone.run(() => {
      const {
        city,
        stateCode,
        zipCode,
        name: street,
        latitude,
        longitude,
        formattedAddress,
        placeId
      } = this.addressService.setupDeliveryData(place);

      this.signalsStoreService.googleAddress.set({
        street,
        city,
        stateCode,
        zipCode,
        latitude,
        longitude,
        placeId,
      });

      this.localStorageService.set(LOCALSTORAGE_KEYS.GOOGLEPLACERESULT, place);
    });


  }

  checkDeliveryAddressCoverage() {

    if (!this.signalsStoreService.googleAddress() || !this.queryString)
      return;

    this.isGoogleAddressFilled.set(true);
    const { street, city, stateCode, latitude, longitude } = this.signalsStoreService.googleAddress();
    const navegoAddressRequest = {
      street,
      city,
      state: stateCode,
      latitude,
      longitude,
    } as NavegoAddressRequest;

    this.addressService.checkDeliveryAddress(navegoAddressRequest, !this.modalContentData()?.fromHome, true);
    this.hasDeliveryAddressCoverage = computed(() => {
      if (!!this.addressService.navegoAddress()) {
        if (!this.modalContentData()?.fromHome) {
          setTimeout(() => {
            this.closeModal();
            this.router.navigate(['/signup'])
          }, 1000);
        } else {
          this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, this.email);
          this.activeModal.close({
            validationComplete: true,
            inDeliveryZone: true
          });
        }
      } else {
        this.localStorageService.remove(LOCALSTORAGE_KEYS.GOOGLEPLACERESULT);
        if (this.modalContentData()?.fromHome) {
          this.activeModal.close({
            validationComplete: true,
            inDeliveryZone: false
          });
        }
      }

      return !!this.addressService.navegoAddress()
    });
  }

  changeAddress() {
    this.hasDeliveryAddressCoverage = signal(false);
    this.isGoogleAddressFilled.set(false);
  }

  closeModal() {
    this.activeModal.close(true);
  }

  onEmailChange(event: any) {
    this.email = event.target.value;
    this.validateEmail(this.email);
  }

  validateEmail(value?: string) {
    const emailValue = value || this.email;
    const validation = this.emailValidation();

    // Check if email is empty
    if (!emailValue.trim()) {
      validation.error = 'Email is required';
      validation.valid = false;
    }
    // Check email format
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(emailValue)) {
      validation.error = 'Please enter a valid email address';
      validation.valid = false;
    }
    // Email is valid
    else {
      validation.error = '';
      validation.valid = true;
    }

    this.emailValidation.set({ ...validation });
  }

}
